// /waitlist — 2027 application landing + 4-step application form.
// Submits to POST /api/waitlist (already live). Confirmation state on success.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSeo } from "@/hooks/useSeo";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { webPageSchema, breadcrumbSchema, organizationSchema } from "@/lib/schemas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api/waitlist`;

const LOOKING_FOR = [
  "Brand / Brand Development",
  "Website",
  "Marketing",
  "Local Marketing / Google Presence",
  "Ongoing Growth Partner",
  "Print / Physical Marketing",
  "AI / Automation",
  "Multiple Services",
  "Not Sure Yet",
  "Other",
];
const BUDGETS = [
  "Under $2,500",
  "$2,500–$5,000",
  "$5,000–$10,000",
  "$10,000–$25,000",
  "$25,000+",
  "Not Sure Yet",
];
const TIMING = [
  "As soon as an opening becomes available",
  "Q1 2027",
  "Q2 2027",
  "Q3 2027",
  "Q4 2027",
  "Flexible",
];
const HEARD_FROM = [
  "Google",
  "Facebook",
  "LinkedIn",
  "Referral",
  "Existing Client",
  "Saw Kostaja's Work",
  "Other",
];

const analyticsEvent = (name, payload = {}) => {
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
  } catch (err) {
    // Analytics failures must never break the flow. Log for debugging only.
    if (typeof console !== "undefined") console.debug("analyticsEvent failed", name, err);
  }
};

const FieldLabel = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 text-white/70">
    {children}
    {required ? " *" : ""}
  </label>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label className={`cursor-pointer block border ${checked ? "border-[#FFC600] bg-[#FFC600]/5" : "border-white/15 hover:border-white/40"} px-4 py-3 transition-colors`}>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      data-testid={testId}
      className="sr-only"
    />
    <span className="text-sm font-medium text-white">{value}</span>
  </label>
);

const StepDots = ({ step, total }) => (
  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-black text-white/60">
    <span>Step {step} of {total}</span>
    <div className="flex gap-1.5 ml-3">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={`step-dot-${i}`}
          className={`block w-6 h-[3px] ${i < step ? "bg-[#FFC600]" : "bg-white/20"}`}
        />
      ))}
    </div>
  </div>
);

const captureAttribution = () => {
  if (typeof window === "undefined") return {};
  const q = new URLSearchParams(window.location.search);
  return {
    utmSource: q.get("utm_source") || null,
    utmMedium: q.get("utm_medium") || null,
    utmCampaign: q.get("utm_campaign") || null,
    utmContent: q.get("utm_content") || null,
    utmTerm: q.get("utm_term") || null,
    referrer: document.referrer || null,
    landingPage: window.location.pathname + window.location.search,
  };
};

const initialForm = {
  name: "",
  businessName: "",
  email: "",
  phone: "",
  websiteOrSocial: "",
  lookingFor: "",
  projectDescription: "",
  budgetRange: "",
  desiredStart: "",
  earlierOpeningConsent: "Yes",
  heardFrom: "",
  website_url: "", // honeypot
};

const Waitlist = () => {
  useSeo({
    title: "2027 Project Waitlist | Work With Kostaja",
    description:
      "Kostaja's 2026 project capacity is allocated. Submit your business or project for consideration for 2027 and priority access to earlier openings.",
    canonical: "https://kostaja.ca/waitlist",
    jsonLd: [
      organizationSchema(),
      webPageSchema({
        path: "/waitlist",
        name: "2027 Project Waitlist",
        description: "Applications open for 2027 projects. Waitlisted businesses may receive priority consideration when an earlier opening becomes available.",
      }),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Waitlist", path: "/waitlist" }]),
    ],
  });

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    analyticsEvent("waitlist_page_view");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const attribution = useMemo(() => captureAttribution(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.name.trim()) e.name = "Required";
      if (!form.businessName.trim()) e.businessName = "Required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
      if (form.phone.trim().length < 3) e.phone = "Required";
      if (!form.websiteOrSocial.trim()) e.websiteOrSocial = "Required";
    }
    if (step === 2) {
      if (!form.lookingFor) e.lookingFor = "Select one";
      if (form.projectDescription.trim().length < 10) e.projectDescription = "A little more detail helps";
    }
    if (step === 3) {
      if (!form.budgetRange) e.budgetRange = "Select one";
      if (!form.desiredStart) e.desiredStart = "Select one";
      if (!form.earlierOpeningConsent) e.earlierOpeningConsent = "Select one";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep()) {
      analyticsEvent("waitlist_step_complete", { step });
      setStep((s) => Math.min(4, s + 1));
      if (step === 1) analyticsEvent("waitlist_form_start");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const payload = { ...form, ...attribution };
      await axios.post(API_URL, payload);
      analyticsEvent("waitlist_form_submit", {
        looking_for: form.lookingFor,
        budget: form.budgetRange,
      });
      setSubmitted(true);
    } catch (err) {
      analyticsEvent("waitlist_form_error");
      setServerError(
        err?.response?.data?.detail ||
          "Something went wrong submitting your application. Please try again or email info@kostaja.ca."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="waitlist-success" className="bg-black text-white">
        <section className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 lg:py-40">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
                <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
                <span>Application received</span>
              </div>
              <h1
                className="mt-8 font-black tracking-[-0.035em] leading-[0.88]"
                style={{ fontSize: "clamp(52px, 8vw, 128px)" }}
              >
                You're on <span className="k-yellow">our radar.</span>
              </h1>
              <p className="mt-8 text-lg lg:text-2xl leading-[1.4] font-medium text-white/85 max-w-2xl">
                We've received your project for consideration. Kostaja reviews upcoming work based on project fit, requirements, timing, and available capacity.
              </p>
              <p className="mt-4 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
                If there's a potential match, we'll be in touch about the next step. 2027 applications are currently under review.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <PrimaryButton to="/portfolio" testId="waitlist-success-portfolio">View Selected Work</PrimaryButton>
                <SecondaryButton to="/" testId="waitlist-success-home">Back to Kostaja.ca</SecondaryButton>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="waitlist-page" className="bg-black text-white">
      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-14 lg:pb-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Waitlist" }]} />
          <div className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
            <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
            <span>2027 applications</span>
          </div>
          <h1
            data-testid="waitlist-hero-headline"
            className="mt-6 font-black tracking-[-0.035em] leading-[0.88]"
            style={{ fontSize: "clamp(52px, 8vw, 128px)" }}
          >
            Work with <span className="k-yellow">Kostaja.</span>
          </h1>
          <p className="mt-8 lg:mt-10 text-lg lg:text-2xl leading-[1.4] font-medium text-white/85 max-w-3xl">
            2026 project capacity has been allocated. Kostaja is now reviewing businesses and organizations for upcoming openings and 2027 projects.
          </p>
          <p className="mt-4 max-w-3xl text-base lg:text-lg text-white/70 leading-relaxed">
            Submit your project for consideration. Waitlisted businesses may receive priority access if an earlier opening becomes available.
          </p>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          {[
            {
              t: "Limited annual intake",
              d: "We deliberately limit the number of new client relationships we accept.",
            },
            {
              t: "Selected by fit",
              d: "Applications are reviewed based on the project, requirements, timing, and fit.",
            },
            {
              t: "Earlier access",
              d: "Waitlisted businesses may be contacted first when earlier capacity becomes available.",
            },
          ].map((p, i) => (
            <div key={p.t}>
              <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[#FFC600]">0{i + 1}</div>
              <h2 className="mt-3 text-2xl lg:text-3xl font-black tracking-[-0.01em] leading-tight">
                {p.t}
              </h2>
              <p className="mt-4 text-white/75 leading-relaxed max-w-sm">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section className="border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
            <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
              <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
              <span>Apply to work with Kostaja</span>
            </div>
            <StepDots step={step} total={4} />
          </div>

          {serverError ? (
            <div data-testid="waitlist-server-error" className="mb-6 p-4 border-2 border-[#FFC600] text-white bg-black">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600] mb-1">There was a problem</div>
              <p className="text-sm">{serverError}</p>
            </div>
          ) : null}

          <form
            data-testid="waitlist-form"
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="border border-white/15 p-6 lg:p-10"
          >
            {/* Honeypot */}
            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={form.website_url}
              onChange={update("website_url")}
              className="hidden"
              aria-hidden="true"
            />

            {step === 1 && (
              <div data-testid="waitlist-step-1" className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <h2 className="sm:col-span-2 text-3xl lg:text-4xl font-black tracking-[-0.02em] mb-2">About you.</h2>
                <div>
                  <FieldLabel htmlFor="wl-name" required>Full name</FieldLabel>
                  <Input id="wl-name" data-testid="wl-name" value={form.name} onChange={update("name")} className="rounded-none border border-white/15 h-12" />
                  {errors.name && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.name}</div>}
                </div>
                <div>
                  <FieldLabel htmlFor="wl-business" required>Business or organization</FieldLabel>
                  <Input id="wl-business" data-testid="wl-business" value={form.businessName} onChange={update("businessName")} className="rounded-none border border-white/15 h-12" />
                  {errors.businessName && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.businessName}</div>}
                </div>
                <div>
                  <FieldLabel htmlFor="wl-email" required>Email</FieldLabel>
                  <Input id="wl-email" type="email" data-testid="wl-email" value={form.email} onChange={update("email")} className="rounded-none border border-white/15 h-12" />
                  {errors.email && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.email}</div>}
                </div>
                <div>
                  <FieldLabel htmlFor="wl-phone" required>Phone</FieldLabel>
                  <Input id="wl-phone" type="tel" data-testid="wl-phone" value={form.phone} onChange={update("phone")} className="rounded-none border border-white/15 h-12" />
                  {errors.phone && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.phone}</div>}
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel htmlFor="wl-web" required>Website or primary social URL</FieldLabel>
                  <Input id="wl-web" data-testid="wl-web" value={form.websiteOrSocial} onChange={update("websiteOrSocial")} placeholder="https://" className="rounded-none border border-white/15 h-12" />
                  {errors.websiteOrSocial && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.websiteOrSocial}</div>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="waitlist-step-2" className="space-y-6">
                <h2 className="text-3xl lg:text-4xl font-black tracking-[-0.02em] mb-2">About the project.</h2>
                <div>
                  <FieldLabel htmlFor="wl-looking" required>What are you looking for?</FieldLabel>
                  <select
                    id="wl-looking"
                    data-testid="wl-looking"
                    value={form.lookingFor}
                    onChange={update("lookingFor")}
                    className="w-full h-12 border border-white/15 px-3 bg-black text-white"
                  >
                    <option value="">Select</option>
                    {LOOKING_FOR.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {errors.lookingFor && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.lookingFor}</div>}
                </div>
                <div>
                  <FieldLabel htmlFor="wl-desc" required>What needs to change?</FieldLabel>
                  <p className="text-xs text-white/60 -mt-1 mb-2">Tell us what you're trying to accomplish, fix, launch, improve, or grow.</p>
                  <Textarea
                    id="wl-desc"
                    data-testid="wl-desc"
                    rows={8}
                    value={form.projectDescription}
                    onChange={update("projectDescription")}
                    className="rounded-none border border-white/15"
                  />
                  {errors.projectDescription && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.projectDescription}</div>}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="waitlist-step-3" className="space-y-8">
                <h2 className="text-3xl lg:text-4xl font-black tracking-[-0.02em]">Timing and investment.</h2>
                <div>
                  <FieldLabel required>Approximate project investment</FieldLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {BUDGETS.map((b) => (
                      <Radio key={b} name="budget" value={b} checked={form.budgetRange === b} onChange={update("budgetRange")} testId={`wl-budget-${b}`} />
                    ))}
                  </div>
                  {errors.budgetRange && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.budgetRange}</div>}
                </div>
                <div>
                  <FieldLabel required>When would you ideally like to begin?</FieldLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TIMING.map((t) => (
                      <Radio key={t} name="timing" value={t} checked={form.desiredStart === t} onChange={update("desiredStart")} testId={`wl-timing-${t}`} />
                    ))}
                  </div>
                  {errors.desiredStart && <div className="text-xs mt-1 font-semibold text-[#FFC600]">{errors.desiredStart}</div>}
                </div>
                <div>
                  <FieldLabel required>If an earlier opening becomes available, should we contact you?</FieldLabel>
                  <div className="grid grid-cols-2 gap-2 max-w-md">
                    {["Yes", "No"].map((v) => (
                      <Radio key={v} name="earlier" value={v} checked={form.earlierOpeningConsent === v} onChange={update("earlierOpeningConsent")} testId={`wl-earlier-${v}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel htmlFor="wl-heard">How did you hear about Kostaja?</FieldLabel>
                  <select
                    id="wl-heard"
                    data-testid="wl-heard"
                    value={form.heardFrom}
                    onChange={update("heardFrom")}
                    className="w-full h-12 border border-white/15 px-3 bg-black text-white"
                  >
                    <option value="">Optional</option>
                    {HEARD_FROM.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="waitlist-step-4" className="space-y-6">
                <h2 className="text-3xl lg:text-4xl font-black tracking-[-0.02em]">Review and submit.</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
                  {[
                    ["Name", form.name],
                    ["Business", form.businessName],
                    ["Email", form.email],
                    ["Phone", form.phone],
                    ["Website/Social", form.websiteOrSocial],
                    ["Looking for", form.lookingFor],
                    ["Budget", form.budgetRange],
                    ["Desired start", form.desiredStart],
                    ["Earlier opening", form.earlierOpeningConsent],
                    ["Heard from", form.heardFrom || "-"],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-black p-4">
                      <dt className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600]">{k}</dt>
                      <dd className="mt-1 text-white text-sm break-words">{v || <span className="text-white/40">Not provided</span>}</dd>
                    </div>
                  ))}
                  <div className="bg-black p-4 sm:col-span-2">
                    <dt className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600]">Project</dt>
                    <dd className="mt-1 text-white text-sm whitespace-pre-wrap">{form.projectDescription}</dd>
                  </div>
                </dl>
                <p className="text-xs text-white/55">
                  Submitting an application does not guarantee project acceptance or availability.
                </p>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                {step > 1 && (
                  <SecondaryButton onClick={back} testId="wl-back">Back</SecondaryButton>
                )}
              </div>
              <div>
                {step < 4 && (
                  <PrimaryButton onClick={next} testId="wl-next">Continue →</PrimaryButton>
                )}
                {step === 4 && (
                  <PrimaryButton onClick={submit} testId="wl-submit">
                    {submitting ? "Submitting..." : "Apply to Work With Kostaja"}
                  </PrimaryButton>
                )}
              </div>
            </div>
          </form>
          <p className="mt-6 text-xs text-white/50 text-center">
            Applications are considered based on fit, requirements, timing, and available capacity rather than strictly by submission date.
          </p>
        </div>
      </section>

      {/* Alt entry: Portfolio */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 flex items-end justify-between gap-8 flex-wrap">
          <div className="max-w-2xl">
            <Overline color="yellow" className="mb-3">Not ready to apply</Overline>
            <h3 className="text-3xl lg:text-4xl font-black tracking-[-0.01em] leading-tight">See how Kostaja works before you apply.</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <SecondaryButton to="/portfolio">View Selected Work</SecondaryButton>
            <Link to="/services" className="text-sm font-semibold text-white/80 hover:text-[#FFC600] underline underline-offset-[6px] decoration-white/25 hover:decoration-[#FFC600] transition-colors">Explore services →</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Waitlist;
